/**
 * ChartStep - Final clinical note generation and editing
 * 
 * Displays generated clinical note with editable sections
 * Matches design of other ChartMind steps
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  ContentCopyOutlined,
  SendOutlined,
  CheckCircleRounded,
  RefreshRounded,
  PictureAsPdfOutlined,
  MicNoneOutlined,
  StopRounded,
  PhoneIphoneRounded,
} from '@mui/icons-material';
import SectionCard from './SectionCard';
import ChartFeedback from './ChartFeedback';
import ConfirmDialog from 'components/common/ConfirmDialog';
import ClinicalReferenceSources from './ClinicalReferenceSources';
import VoiceLevelIndicator from 'components/voice/VoiceLevelIndicator';
import useEncounterRecording from 'hooks/useEncounterRecording';

// ============================================================================
// Types
// ============================================================================

interface Section {
  key: string;
  title: string;
  placeholder?: string;
}

interface Template {
  name: string;
  sections: Section[];
}

interface ChartStepProps {
  noteSections: Record<string, string>;
  template: Template;
  feedbackRating?: number | null;
  feedbackRemarks?: string;
  loading: boolean;
  error: string | null;
  onUpdateSection?: (key: string, value: string) => void;
  onFeedbackRatingChange?: (rating: number | null) => void;
  onFeedbackRemarksChange?: (remarks: string) => void;
  onCopyToClipboard?: () => Promise<void>;
  onExportToPdf?: () => Promise<void>;
  onRegenerate?: () => void | Promise<void>;
  /** Note on screen predates the current transcript/diagnoses — offer a regenerate. */
  isStale?: boolean;
  /** Note was generated with no diagnoses selected — offer the earlier steps. */
  generatedWithoutDiagnoses?: boolean;
  clinicalReferences?: any[];
  embeddedDocuments?: any[];
  /**
   * The encounter's spoken language, from the Record step's selector.
   *
   * Dictation here is the same recogniser the encounter was captured with, so a clinician
   * working in Swahili does not get their addendum transcribed as English.
   */
  language?: string;
  /**
   * Dictating this section from the paired phone. Same wire as the encounter microphone, routed
   * to one section instead — see CHARTMIND-PHONE-DICTATION.md.
   */
  phoneDictation?: {
    available: boolean;
    target: { key: string; title: string } | null;
    text: string;
    recording: boolean;
    begin: (key: string, title: string, seed: string) => void;
    cancel: () => void;
  };
  /** Set when a partner system (e.g. a clinic's HMIS) sent this patient in. */
  partnerName?: string;
  onSendToPartner?: () => Promise<void>;
  /** Heading override, e.g. for MorningReport's teaching cases. */
  heading?: { title: string; subtitle: string };
  /** Off for MorningReport, where learners type their notes. */
  allowDictation?: boolean;
}


// ============================================================================
// Main Component
// ============================================================================

const ChartStep: React.FC<ChartStepProps> = ({
  noteSections,
  template,
  feedbackRating = null,
  feedbackRemarks = '',
  loading,
  error,
  onUpdateSection,
  onFeedbackRatingChange,
  onFeedbackRemarksChange,
  onCopyToClipboard,
  onExportToPdf,
  onRegenerate,
  isStale = false,
  generatedWithoutDiagnoses = false,
  clinicalReferences = [],
  embeddedDocuments = [],
  language = 'en-US',
  phoneDictation,
  partnerName,
  onSendToPartner,
  heading,
  allowDictation = true,
}) => {
  const theme = useTheme();
  const [copySuccess, setCopySuccess] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);

  /**
   * DICTATION INTO A NOTE SECTION.
   *
   * ⚠️ THIS IS NOT A SECOND ENCOUNTER RECORDER, and the difference matters. The Record step
   * captures the consultation and everything downstream is generated from it. This captures a
   * clinician talking to their own note — "add that the wound was clean and dry" — and the text
   * lands in one section and nowhere else. It regenerates nothing, so an edit made by voice is
   * exactly as safe as one made by typing.
   *
   * Same recorder as the Record step (server-side transcription, so it works in every browser)
   * and the same language, so an addendum cannot arrive in a different language from the note
   * it is being added to.
   */
  const dictation = useEncounterRecording({ language });
  // Held separately from `dictation.isRecording` because it has to stay true through the drain:
  // stopRecording waits for the segments already out for transcription, and the field must keep
  // showing the live text until the last of them lands rather than flicking back to the old
  // content and then updating again.
  const [dictating, setDictating] = useState(false);
  const [dictationStarting, setDictationStarting] = useState(false);
  const dictationStartingRef = useRef(false);
  // ⚠️ STOPPING IS NOT INSTANT AND THE BUTTON HAS TO SAY SO. stopRecording waits for the
  // segments already out for server-side transcription, which is seconds, not milliseconds. A
  // Stop button that looks idle the whole time reads as a button that did nothing, and the
  // clinician presses it again.
  const [dictationStopping, setDictationStopping] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [sendingToPartner, setSendingToPartner] = useState(false);
  const [partnerSent, setPartnerSent] = useState(false);
  const [partnerError, setPartnerError] = useState<string | null>(null);

  const sections = template?.sections || [];
  const hasNote = noteSections && Object.keys(noteSections).length > 0;

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      await onCopyToClipboard?.();
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [onCopyToClipboard]);

  const handleEditSection = useCallback((sectionKey: string, currentContent: string, title: string) => {
    setModalTitle(title);
    setEditingSection(sectionKey);
    setEditedContent(currentContent);
  }, []);

  /**
   * Open the microphone, then seed what is already written.
   *
   * ⚠️ THE ORDER IS THE SAME LESSON THE PHONE PAGE LEARNED, so do not tidy it back. The
   * permission prompt lives inside startRecording and can sit unanswered for as long as the
   * clinician takes, so nothing may claim to be recording until it resolves — otherwise a
   * second tap lands on a Stop button for a recording that never began.
   *
   * `seed` is passed rather than read from state because the card's microphone opens the editor
   * and starts dictating in one gesture, and `editedContent` has not been applied yet at that
   * point. Seeding is what makes speech ADD to the section instead of replacing it.
   */
  const startDictation = useCallback(async (seed: string) => {
    if (dictationStartingRef.current || dictating) return;
    dictationStartingRef.current = true;
    setDictationStarting(true);
    try {
      const capturing = await dictation.startRecording();
      // False means refused or unavailable, and the hook has already set copy we render below.
      if (!capturing) return;
      if (seed) dictation.updateTranscript(seed);
      setDictating(true);
    } finally {
      dictationStartingRef.current = false;
      setDictationStarting(false);
    }
  }, [dictating, dictation]);

  /** Stop and hand back the finished text. Awaits the drain, so the tail is never dropped. */
  const finishDictation = useCallback(async () => {
    await dictation.stopRecording();
    const finalText = dictation.getFinalTranscript();
    setDictating(false);
    dictation.resetRecording();
    return finalText;
  }, [dictation]);

  const handleStopDictation = useCallback(async () => {
    setDictationStopping(true);
    try {
      const finalText = await finishDictation();
      setEditedContent(finalText);
    } finally {
      setDictationStopping(false);
    }
  }, [finishDictation]);

  /**
   * The microphone on a section card.
   *
   * ⚠️ IT ONLY AUTO-STARTS WHEN THERE IS NO CHOICE TO MAKE. With no phone paired, the only
   * microphone is this computer's, so opening the editor and starting it in one gesture is the
   * whole point of the icon. With a phone paired there are two microphones and the clinician has
   * a real preference — and claiming the desktop mic before they can express it is worse than
   * making them press once more, because the useful answer is usually the phone.
   */
  const handleDictateSection = useCallback(
    (sectionKey: string, currentContent: string, title: string) => {
      handleEditSection(sectionKey, currentContent, title);
      if (!phoneDictation?.available) void startDictation(currentContent);
    },
    [handleEditSection, startDictation, phoneDictation],
  );

  // This section is the one the phone is armed for. Two editors can never be open at once, but
  // comparing keys rather than trusting "something is armed" keeps that an assertion, not a hope.
  const phoneOnThisSection =
    Boolean(editingSection) && phoneDictation?.target?.key === editingSection;

  const handleSaveEdit = useCallback(async () => {
    // A phone round is the authority when one is armed: the field has been showing its text, and
    // that is what the clinician just read and accepted.
    if (phoneOnThisSection) {
      const relayed = phoneDictation?.text ?? '';
      phoneDictation?.cancel();
      if (editingSection) onUpdateSection?.(editingSection, relayed);
      setEditingSection(null);
      setEditedContent('');
      return;
    }
    // Saving mid-sentence is a normal way to finish, so it collects the dictation rather than
    // discarding whatever had not been written back to the field yet.
    let content = editedContent;
    if (dictating) {
      setSavingEdit(true);
      try {
        content = await finishDictation();
      } finally {
        setSavingEdit(false);
      }
    }
    if (editingSection) {
      onUpdateSection?.(editingSection, content);
    }
    setEditingSection(null);
    setEditedContent('');
  }, [
    editingSection,
    editedContent,
    dictating,
    finishDictation,
    onUpdateSection,
    phoneOnThisSection,
    phoneDictation,
  ]);

  const handleCancelEdit = useCallback(() => {
    // The microphone has to be handed back even though the text is being thrown away, or it
    // stays open on a dialog that is no longer on screen. Same for the phone's: cancel() stops a
    // round still running, so the clinician is not left holding a phone recording into nothing.
    if (dictating) {
      void dictation.stopRecording();
      setDictating(false);
    }
    dictation.resetRecording();
    dictation.clearError();
    phoneDictation?.cancel();
    setEditingSection(null);
    setEditedContent('');
  }, [dictating, dictation, phoneDictation]);

  const handleConfirmRegenerate = useCallback(() => {
    setRegenerateConfirmOpen(false);
    void onRegenerate?.();
  }, [onRegenerate]);

  const handleFeedbackSubmit = useCallback((rating: number) => {
    onFeedbackRatingChange?.(rating);
  }, [onFeedbackRatingChange]);

  const handleSendToPartner = useCallback(async () => {
    if (!onSendToPartner) return;
    setPartnerError(null);
    setPartnerSent(false);
    setSendingToPartner(true);
    try {
      await onSendToPartner();
      setPartnerSent(true);
    } catch (err) {
      setPartnerError(err instanceof Error ? err.message : 'Could not send the visit.');
    } finally {
      setSendingToPartner(false);
    }
  }, [onSendToPartner]);

  const handleExportPdf = useCallback(async () => {
    if (!onExportToPdf) return;

    setExportError(null);
    setExportingPdf(true);
    try {
      await onExportToPdf();
    } catch (err) {
      console.error('Failed to export PDF:', err);
      setExportError(
        err instanceof Error ? err.message : 'Failed to export clinical note to PDF.',
      );
    } finally {
      setExportingPdf(false);
    }
  }, [onExportToPdf]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1.5 }}>
          {heading?.title ?? "Clinical Note"}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {heading?.subtitle ??
          "Review and edit your clinical documentation. Click edit to customize any section."}
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ width: '100%', mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={onRegenerate} startIcon={<RefreshRounded />}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Generating clinical note...
          </Typography>
        </Box>
      )}

      {/* Note is out of date relative to the current transcript/diagnoses. Regenerates straight
          away rather than routing through the usual confirm: there are no edits worth protecting
          in a note that no longer matches its inputs. */}
      {!loading && hasNote && isStale && (
        <Alert
          severity="warning"
          sx={{ width: '100%', mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshRounded />}
              onClick={() => void onRegenerate?.()}
            >
              Regenerate
            </Button>
          }
        >
          This note was generated before your latest changes.
        </Alert>
      )}

      {/* Generated straight from the transcript. Not a problem, just the thinnest version of the
          note, so point at what would make it richer. */}
      {!loading && hasNote && !isStale && generatedWithoutDiagnoses && (
        <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
          This note was written from the transcript alone. For more detail, please go back and
          choose diagnoses and treatments, then regenerate.
        </Alert>
      )}

      {/* Generated note */}
      {!loading && hasNote && (
        <>
          {/* Action buttons */}
          {/* Tight under the buttons rather than the usual mb={3}: the rating row that follows
              is part of this group, and a full gap made it read as the note's first line. */}
          <Box display="flex" gap={1} justifyContent="flex-end" mb={1.5} flexWrap="wrap">
            {onSendToPartner && (
              <LoadingButton
                variant="contained"
                size="small"
                startIcon={<SendOutlined />}
                onClick={() => void handleSendToPartner()}
                loading={sendingToPartner}
              >
                {`Send to ${partnerName || 'referring system'}`}
              </LoadingButton>
            )}
            <LoadingButton
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdfOutlined />}
              onClick={() => void handleExportPdf()}
              loading={exportingPdf}
              disabled={!onExportToPdf}
            >
              Export to PDF
            </LoadingButton>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshRounded />}
              onClick={() => setRegenerateConfirmOpen(true)}
            >
              Regenerate notes
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={copySuccess ? <CheckCircleRounded /> : <ContentCopyOutlined />}
              onClick={handleCopy}
              color={copySuccess ? 'success' : 'primary'}
            >
              {copySuccess ? 'Copied!' : 'Copy'}
            </Button>
          </Box>

          {/* Directly under the buttons, sharing their right edge. Below the note it went
              unrated every single time — see ChartFeedback. */}
          <ChartFeedback
            key={String(feedbackRating)}
            defaultRating={feedbackRating}
            onSubmit={handleFeedbackSubmit}
          />

          {partnerSent && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPartnerSent(false)}>
              {`Sent to ${partnerName || 'the referring system'}. Sending again replaces it.`}
            </Alert>
          )}

          {partnerError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPartnerError(null)}>
              {partnerError}
            </Alert>
          )}

          {exportError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
              {exportError}
            </Alert>
          )}

          {/* Sections */}
          <Box sx={{ minHeight: 400 }}>
            {sections.map((section, index) => {
              const content = noteSections[section.key] || '';
              // Hide sections the note left blank (e.g. when an org's rules omit
              // empty sections). Sections with content always render.
              if (!content.trim()) return null;
              const colors = [
                theme.palette.primary.main,
                theme.palette.info.main,
                theme.palette.success.main,
                theme.palette.warning.main,
                theme.palette.secondary.main,
                theme.palette.error.main,
              ];
              return (
                <SectionCard
                  key={section.key}
                  title={section.title}
                  content={content}
                  placeholder={section.placeholder}
                  accentColor={colors[index % colors.length]}
                  onEdit={() => handleEditSection(section.key, content, section.title)}
                  onDictate={
                    allowDictation && dictation.isSupported
                      ? () => handleDictateSection(section.key, content, section.title)
                      : undefined
                  }
                />
              );
            })}
          </Box>

          {/* Source documents that backed the note */}
          <ClinicalReferenceSources
            clinicalReferences={clinicalReferences}
            embeddedDocuments={embeddedDocuments}
          />
        </>
      )}

      {/* No note yet. Reaching this is now the exception, not the entry point: arriving at this
          step starts generation on its own, so this only shows when a run came back empty. Kept
          as a manual recovery rather than a dead end. */}
      {!loading && !hasNote && !error && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            No note was generated. The transcript may need more clinical information.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => void onRegenerate?.()}
            disabled={!onRegenerate}
            startIcon={<RefreshRounded />}
          >
            Try Again
          </Button>
        </Box>
      )}

      {/* Edit Modal */}
      <Dialog
        open={editingSection !== null}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            {modalTitle}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Read-only while dictating: one writer at a time is what keeps "what did my cursor
              do to the sentence the recogniser was halfway through" from being a question. */}
          {/* ⚠️ minRows/maxRows RATHER THAN A FIXED rows. A fixed twelve rows gave a one-line
              section the same dialog as a full history, so most edits opened onto a wall of
              empty box with the buttons pushed off the fold. */}
          <TextField
            fullWidth
            multiline
            minRows={5}
            maxRows={14}
            value={
              phoneOnThisSection
                ? phoneDictation?.text ?? ''
                : dictating
                  ? dictation.transcript
                  : editedContent
            }
            onChange={(e) => setEditedContent(e.target.value)}
            placeholder={sections.find(s => s.key === editingSection)?.placeholder}
            InputProps={{ readOnly: dictating || phoneOnThisSection }}
            sx={{ mt: 1 }}
          />

          {dictation.error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={dictation.clearError}>
              {dictation.error}
            </Alert>
          )}

          {/* THE PAIRED PHONE AS THIS SECTION'S MICROPHONE.
              It takes over the dialog while armed: the field goes read-only and shows what the
              phone is sending, which is the same contract desktop dictation already has. */}
          {phoneOnThisSection && (
            <Alert severity="info" icon={false} sx={{ mt: 2 }}>
              {phoneDictation?.recording
                ? `Recording on your phone into ${phoneDictation?.target?.title}. Press stop on your phone when you are done.`
                : `Your phone is ready. Please tap the microphone on it to dictate ${phoneDictation?.target?.title ?? 'this section'}.`}
            </Alert>
          )}

          {allowDictation && dictation.isSupported && !phoneOnThisSection && (
            <Box sx={{ mt: 2 }}>
              {/* ⚠️ THE ROW HOLDS ITS HEIGHT WHETHER OR NOT ANYTHING IS IN IT. The meter and the
                  transcribing note appear and vanish as dictation starts and stops, and letting
                  the row collapse around them moved the dialog's buttons under the cursor
                  mid-press. Fixed height, contents swap inside it. */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ minHeight: 34 }}
              >
                <LoadingButton
                  variant="outlined"
                  size="small"
                  color={dictating ? 'error' : 'primary'}
                  loading={dictationStarting || dictationStopping}
                  // Keeps the label visible while the spinner runs, which is the whole point:
                  // "Stopping" is the state the clinician needs to read.
                  loadingPosition="start"
                  startIcon={dictating ? <StopRounded /> : <MicNoneOutlined />}
                  disabled={savingEdit}
                  onClick={() =>
                    dictating ? void handleStopDictation() : void startDictation(editedContent)
                  }
                  sx={{ flexShrink: 0 }}
                >
                  {dictationStopping ? 'Stopping' : dictating ? 'Stop' : 'Dictate'}
                </LoadingButton>

                {/* Side by side with Dictate, because they are the same action with two
                    microphones — not a primary and a fallback. Hidden once desktop dictation is
                    running: handing the section to the phone mid-sentence would leave two
                    recorders writing one field. */}
                {phoneDictation?.available && !dictating && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PhoneIphoneRounded />}
                    disabled={savingEdit || dictationStarting}
                    sx={{ flexShrink: 0 }}
                    onClick={() => {
                      const section = sections.find((s) => s.key === editingSection);
                      if (editingSection) {
                        phoneDictation.begin(
                          editingSection,
                          section?.title || modalTitle,
                          editedContent,
                        );
                      }
                    }}
                  >
                    Dictate on your phone
                  </Button>
                )}

                {/* ⚠️ THE METER EARNS ITS PLACE. Transcription is server-side and arrives in
                    segments, so there are a few seconds between speaking and seeing words — long
                    enough to read as broken. The bars move with the voice immediately, which is
                    what actually answers "is this thing on". Same component the Record step and
                    CareStart use. */}
                {dictating && !dictationStopping && (
                  <VoiceLevelIndicator level={dictation.micLevel} height={20} />
                )}

                {dictation.pendingSegments > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Transcribing...
                  </Typography>
                )}
              </Stack>

              {/* On its own line. Sharing the row with the button meant this wrapped to two
                  lines at dialog width and then vanished when dictation started, so the row
                  changed height at the exact moment the clinician was looking at it. */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                {dictating
                  ? 'Speak now. The text is added to this section when you stop.'
                  : phoneDictation?.available
                    ? 'Record on this computer or on your paired phone. Your existing text will be kept.'
                    : 'Dictation adds to this section. Your existing text is kept.'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={savingEdit}>
            Close
          </Button>
          {/* ⚠️ LoadingButton, BECAUSE SAVING MID-DICTATION IS NOT INSTANT. It stops the
              recorder and waits for the segments still out for transcription, so without this
              the dialog sits there looking like a button that did nothing. */}
          <LoadingButton
            onClick={() => void handleSaveEdit()}
            variant="contained"
            loading={savingEdit}
            // A stop already draining owns the recorder; saving on top of it would start a
            // second drain for the same segments.
            disabled={dictationStopping}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={regenerateConfirmOpen}
        onClose={() => setRegenerateConfirmOpen(false)}
        title="Regenerate clinical note?"
        message="This replaces the entire note with a new AI-generated draft. Any edits you made to sections will be lost."
        confirmLabel="Regenerate notes"
        onConfirm={handleConfirmRegenerate}
        confirmColor="warning"
      />
    </Box>
  );
};

export default ChartStep;
